import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types/base';
import {
  LenderResponse,
  LenderFeatureResponse,
  LenderPolicyResponse,
  LenderProductResponse,
  LenderProductPricingResponse,
  CreateLenderDto,
  UpdateLenderDto,
  CreateLenderFeatureDto,
  UpdateLenderFeatureDto,
  CreateLenderPolicyDto,
  UpdateLenderPolicyDto,
  CreateLenderProductDto,
  UpdateLenderProductDto,
  CreateLenderProductPricingDto,
  UpdateLenderProductPricingDto,
  ApplicantType,
  LenderProductProfileResponse,
  CreateLenderProductProfileDto,
  UpdateLenderProductProfileDto,
  LenderProfilesGroup,
} from '@/pages/settings/types/lender.types';
import {
  GetLenderRatesArgs,
  LenderRatesResponse,
} from '@/pages/settings/types/lender-rates.types';

const BASE = '/v1/lender-management/lenders';

/** Broker-facing rate grid. Unauthenticated, hence the separate `public` path. */
const RATES_BASE = '/v1/public/lender-management/rates';

/**
 * Serialises rate-grid arguments to the wire format.
 *
 * Multi-value filters are **comma-separated inside one param** — `?terms=0,12`
 * — not repeated params, which is what `URLSearchParams` would produce if the
 * arrays were passed through untouched.
 *
 * Empty arrays, `null` and `undefined` are all dropped: the API treats an
 * absent param as "no filter on this dimension", which is exactly what an
 * unticked filter group means.
 */
const toRatesParams = (args: GetLenderRatesArgs): Record<string, string> => {
  const params: Record<string, string> = {};

  const csv = (key: string, values?: readonly unknown[]) => {
    if (values?.length) params[key] = values.join(',');
  };
  const scalar = (key: string, value?: string | number | null) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = String(value);
    }
  };

  scalar('loanAmount', args.loanAmount);
  scalar('lvr', args.lvr);
  csv('terms', args.terms);
  csv('occupancyType', args.occupancyType);
  csv('repaymentType', args.repaymentType);
  csv('isPackage', args.isPackage);
  scalar('applicantType', args.applicantType);
  csv('lenderIds', args.lenderIds);
  scalar('asOf', args.asOf);
  scalar('sortBy', args.sortBy);
  scalar('sortDir', args.sortDir);
  // Sent even when false — the API's own default may differ from the UI's.
  if (args.includeFuture !== undefined) {
    params.includeFuture = String(args.includeFuture);
  }

  return params;
};

export const lendersApi = createApi({
  reducerPath: 'lendersApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'Lender',
    'LenderFeature',
    'LenderPolicy',
    'LenderProduct',
    'LenderPricing',
    'LenderProductProfile',
    'LenderRates',
  ],
  endpoints: (builder) => ({
    // ── Public rate comparison grid ──
    getLenderRates: builder.query<
      ApiResponse<LenderRatesResponse>,
      GetLenderRatesArgs
    >({
      query: (args) => ({ url: RATES_BASE, params: toRatesParams(args) }),
      providesTags: [{ type: 'LenderRates', id: 'LIST' }],
    }),

    // ── Lenders CRUD ──
    getLenders: builder.query<ApiResponse<LenderResponse[]>, void>({
      query: () => BASE,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'Lender' as const,
                id,
              })),
              { type: 'Lender', id: 'LIST' },
            ]
          : [{ type: 'Lender', id: 'LIST' }],
    }),

    getLender: builder.query<ApiResponse<LenderResponse>, string>({
      query: (id) => `${BASE}/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Lender', id }],
    }),

    createLender: builder.mutation<
      ApiResponse<LenderResponse>,
      CreateLenderDto
    >({
      query: (body) => ({ url: BASE, method: 'POST', body }),
      invalidatesTags: [{ type: 'Lender', id: 'LIST' }],
    }),

    updateLender: builder.mutation<
      ApiResponse<LenderResponse>,
      { id: string } & UpdateLenderDto
    >({
      query: ({ id, ...body }) => ({
        url: `${BASE}/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Lender', id },
        { type: 'Lender', id: 'LIST' },
      ],
    }),

    deleteLender: builder.mutation<void, string>({
      query: (id) => ({ url: `${BASE}/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Lender', id: 'LIST' }],
    }),

    uploadLenderLogo: builder.mutation<
      ApiResponse<LenderResponse>,
      { id: string; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${BASE}/${id}/logo`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Lender', id },
        { type: 'Lender', id: 'LIST' },
      ],
    }),

    deleteLenderLogo: builder.mutation<ApiResponse<LenderResponse>, string>({
      query: (id) => ({
        url: `${BASE}/${id}/logo`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Lender', id },
        { type: 'Lender', id: 'LIST' },
      ],
    }),

    // ── Features ──
    getLenderFeatures: builder.query<
      ApiResponse<LenderFeatureResponse>,
      string
    >({
      query: (lenderId) => `${BASE}/${lenderId}/features`,
      providesTags: (_r, _e, lenderId) => [
        { type: 'LenderFeature', id: lenderId },
      ],
    }),

    getLenderFeaturesHistory: builder.query<
      ApiResponse<LenderFeatureResponse[]>,
      string
    >({
      query: (lenderId) => `${BASE}/${lenderId}/features/history`,
      providesTags: (_r, _e, lenderId) => [
        { type: 'LenderFeature', id: `${lenderId}-history` },
      ],
    }),

    createLenderFeatures: builder.mutation<
      ApiResponse<LenderFeatureResponse>,
      { lenderId: string } & Omit<CreateLenderFeatureDto, 'lenderId'>
    >({
      query: ({ lenderId, ...body }) => ({
        url: `${BASE}/${lenderId}/features`,
        method: 'POST',
        body: { ...body, lenderId },
      }),
      invalidatesTags: (_r, _e, { lenderId }) => [
        { type: 'LenderFeature', id: lenderId },
        { type: 'LenderFeature', id: `${lenderId}-history` },
        { type: 'Lender', id: lenderId },
      ],
    }),

    updateLenderFeatures: builder.mutation<
      ApiResponse<LenderFeatureResponse>,
      { lenderId: string } & UpdateLenderFeatureDto
    >({
      query: ({ lenderId, ...body }) => ({
        url: `${BASE}/${lenderId}/features`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { lenderId }) => [
        { type: 'LenderFeature', id: lenderId },
        { type: 'LenderFeature', id: `${lenderId}-history` },
      ],
    }),

    deleteLenderFeatures: builder.mutation<void, string>({
      query: (lenderId) => ({
        url: `${BASE}/${lenderId}/features`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, lenderId) => [
        { type: 'LenderFeature', id: lenderId },
        { type: 'LenderFeature', id: `${lenderId}-history` },
        { type: 'Lender', id: lenderId },
      ],
    }),

    // ── Policy ──
    getLenderPolicy: builder.query<ApiResponse<LenderPolicyResponse>, string>({
      query: (lenderId) => `${BASE}/${lenderId}/policy`,
      providesTags: (_r, _e, lenderId) => [
        { type: 'LenderPolicy', id: lenderId },
      ],
    }),

    getLenderPolicyHistory: builder.query<
      ApiResponse<LenderPolicyResponse[]>,
      string
    >({
      query: (lenderId) => `${BASE}/${lenderId}/policy/history`,
      providesTags: (_r, _e, lenderId) => [
        { type: 'LenderPolicy', id: `${lenderId}-history` },
      ],
    }),

    createLenderPolicy: builder.mutation<
      ApiResponse<LenderPolicyResponse>,
      { lenderId: string } & CreateLenderPolicyDto
    >({
      query: ({ lenderId, ...body }) => ({
        url: `${BASE}/${lenderId}/policy`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { lenderId }) => [
        { type: 'LenderPolicy', id: lenderId },
        { type: 'LenderPolicy', id: `${lenderId}-history` },
        { type: 'Lender', id: lenderId },
      ],
    }),

    updateLenderPolicy: builder.mutation<
      ApiResponse<LenderPolicyResponse>,
      { lenderId: string } & UpdateLenderPolicyDto
    >({
      query: ({ lenderId, ...body }) => ({
        url: `${BASE}/${lenderId}/policy`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { lenderId }) => [
        { type: 'LenderPolicy', id: lenderId },
        { type: 'LenderPolicy', id: `${lenderId}-history` },
      ],
    }),

    // ── Products ──
    getLenderProducts: builder.query<
      ApiResponse<LenderProductResponse[]>,
      { lenderId: string; applicantType?: ApplicantType }
    >({
      query: ({ lenderId, applicantType }) =>
        applicantType
          ? `${BASE}/${lenderId}/products?applicantType=${applicantType}`
          : `${BASE}/${lenderId}/products`,
      providesTags: (result, _e, { lenderId }) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'LenderProduct' as const,
                id,
              })),
              { type: 'LenderProduct', id: lenderId },
            ]
          : [{ type: 'LenderProduct', id: lenderId }],
    }),

    createLenderProduct: builder.mutation<
      ApiResponse<LenderProductResponse>,
      { lenderId: string } & CreateLenderProductDto
    >({
      query: ({ lenderId, ...body }) => ({
        url: `${BASE}/${lenderId}/products`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { lenderId }) => [
        { type: 'LenderProduct', id: lenderId },
        { type: 'LenderRates', id: 'LIST' },
      ],
    }),

    updateLenderProduct: builder.mutation<
      ApiResponse<LenderProductResponse>,
      { lenderId: string; productId: string } & UpdateLenderProductDto
    >({
      query: ({ lenderId, productId, ...body }) => ({
        url: `${BASE}/${lenderId}/products/${productId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { lenderId, productId }) => [
        { type: 'LenderProduct', id: productId },
        { type: 'LenderProduct', id: lenderId },
        { type: 'LenderRates', id: 'LIST' },
      ],
    }),

    deleteLenderProduct: builder.mutation<
      void,
      { lenderId: string; productId: string }
    >({
      query: ({ lenderId, productId }) => ({
        url: `${BASE}/${lenderId}/products/${productId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { lenderId }) => [
        { type: 'LenderProduct', id: lenderId },
        { type: 'LenderRates', id: 'LIST' },
      ],
    }),

    // ── Product Pricings ──
    getLenderProductPricings: builder.query<
      ApiResponse<LenderProductPricingResponse[]>,
      { lenderId: string; productId: string }
    >({
      query: ({ lenderId, productId }) =>
        `${BASE}/${lenderId}/products/${productId}/pricings`,
      providesTags: (result, _e, { productId }) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'LenderPricing' as const,
                id,
              })),
              { type: 'LenderPricing', id: productId },
            ]
          : [{ type: 'LenderPricing', id: productId }],
    }),

    createLenderProductPricing: builder.mutation<
      ApiResponse<LenderProductPricingResponse>,
      {
        lenderId: string;
        productId: string;
      } & CreateLenderProductPricingDto
    >({
      query: ({ lenderId, productId, ...body }) => ({
        url: `${BASE}/${lenderId}/products/${productId}/pricings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { productId, lenderId }) => [
        { type: 'LenderPricing', id: productId },
        { type: 'LenderProduct', id: lenderId },
        { type: 'LenderRates', id: 'LIST' },
      ],
    }),

    updateLenderProductPricing: builder.mutation<
      ApiResponse<LenderProductPricingResponse>,
      {
        lenderId: string;
        productId: string;
        pricingId: string;
      } & UpdateLenderProductPricingDto
    >({
      query: ({ lenderId, productId, pricingId, ...body }) => ({
        url: `${BASE}/${lenderId}/products/${productId}/pricings/${pricingId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { productId, pricingId, lenderId }) => [
        { type: 'LenderPricing', id: pricingId },
        { type: 'LenderPricing', id: productId },
        { type: 'LenderProduct', id: lenderId },
        { type: 'LenderRates', id: 'LIST' },
      ],
    }),

    deleteLenderProductPricing: builder.mutation<
      void,
      { lenderId: string; productId: string; pricingId: string }
    >({
      query: ({ lenderId, productId, pricingId }) => ({
        url: `${BASE}/${lenderId}/products/${productId}/pricings/${pricingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { productId, lenderId }) => [
        { type: 'LenderPricing', id: productId },
        { type: 'LenderProduct', id: lenderId },
        { type: 'LenderRates', id: 'LIST' },
      ],
    }),

    // ── Product Profiles (unified expat / foreigner) ──

    // Comparison view across all lenders (applicantType REQUIRED) — call once per tab/type.
    getProductProfilesComparison: builder.query<
      ApiResponse<LenderProfilesGroup[]>,
      { applicantType: ApplicantType }
    >({
      query: ({ applicantType }) =>
        `${BASE}/product-profiles?applicantType=${applicantType}`,
      providesTags: (result, _e, { applicantType }) =>
        result?.data
          ? [
              ...result.data.flatMap((g) =>
                g.profiles.map((p) => ({
                  type: 'LenderProductProfile' as const,
                  id: p.id,
                })),
              ),
              {
                type: 'LenderProductProfile' as const,
                id: `ALL-${applicantType}`,
              },
            ]
          : [
              {
                type: 'LenderProductProfile' as const,
                id: `ALL-${applicantType}`,
              },
            ],
    }),

    // Current profiles for one lender (applicantType optional filter).
    getLenderProductProfiles: builder.query<
      ApiResponse<LenderProductProfileResponse[]>,
      { lenderId: string; applicantType?: ApplicantType }
    >({
      query: ({ lenderId, applicantType }) =>
        applicantType
          ? `${BASE}/${lenderId}/product-profiles?applicantType=${applicantType}`
          : `${BASE}/${lenderId}/product-profiles`,
      providesTags: (result, _e, { lenderId, applicantType }) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'LenderProductProfile' as const,
                id,
              })),
              {
                type: 'LenderProductProfile' as const,
                id: `${lenderId}-${applicantType ?? 'ALL'}`,
              },
            ]
          : [
              {
                type: 'LenderProductProfile' as const,
                id: `${lenderId}-${applicantType ?? 'ALL'}`,
              },
            ],
    }),

    // All profiles for one lender, including history.
    getLenderProductProfilesHistory: builder.query<
      ApiResponse<LenderProductProfileResponse[]>,
      string
    >({
      query: (lenderId) => `${BASE}/${lenderId}/product-profiles/history`,
      providesTags: (_r, _e, lenderId) => [
        { type: 'LenderProductProfile', id: `${lenderId}-history` },
      ],
    }),

    getLenderProductProfile: builder.query<
      ApiResponse<LenderProductProfileResponse>,
      { lenderId: string; profileId: string }
    >({
      query: ({ lenderId, profileId }) =>
        `${BASE}/${lenderId}/product-profiles/${profileId}`,
      providesTags: (_r, _e, { profileId }) => [
        { type: 'LenderProductProfile', id: profileId },
      ],
    }),

    createLenderProductProfile: builder.mutation<
      ApiResponse<LenderProductProfileResponse>,
      { lenderId: string } & CreateLenderProductProfileDto
    >({
      query: ({ lenderId, ...body }) => ({
        url: `${BASE}/${lenderId}/product-profiles`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { lenderId, applicantType }) => [
        { type: 'LenderProductProfile', id: `${lenderId}-${applicantType}` },
        { type: 'LenderProductProfile', id: `${lenderId}-ALL` },
        { type: 'LenderProductProfile', id: `${lenderId}-history` },
        { type: 'LenderProductProfile', id: `ALL-${applicantType}` },
      ],
    }),

    updateLenderProductProfile: builder.mutation<
      ApiResponse<LenderProductProfileResponse>,
      {
        lenderId: string;
        profileId: string;
        applicantType: ApplicantType;
      } & UpdateLenderProductProfileDto
    >({
      // applicantType is sent only to drive cache invalidation; the backend
      // strips it (whitelist) since it cannot change on update.
      query: ({ lenderId, profileId, ...body }) => ({
        url: `${BASE}/${lenderId}/product-profiles/${profileId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { lenderId, profileId, applicantType }) => [
        { type: 'LenderProductProfile', id: profileId },
        { type: 'LenderProductProfile', id: `${lenderId}-${applicantType}` },
        { type: 'LenderProductProfile', id: `${lenderId}-ALL` },
        { type: 'LenderProductProfile', id: `${lenderId}-history` },
        { type: 'LenderProductProfile', id: `ALL-${applicantType}` },
      ],
    }),

    deleteLenderProductProfile: builder.mutation<
      void,
      { lenderId: string; profileId: string; applicantType: ApplicantType }
    >({
      query: ({ lenderId, profileId }) => ({
        url: `${BASE}/${lenderId}/product-profiles/${profileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { lenderId, profileId, applicantType }) => [
        { type: 'LenderProductProfile', id: profileId },
        { type: 'LenderProductProfile', id: `${lenderId}-${applicantType}` },
        { type: 'LenderProductProfile', id: `${lenderId}-ALL` },
        { type: 'LenderProductProfile', id: `${lenderId}-history` },
        { type: 'LenderProductProfile', id: `ALL-${applicantType}` },
      ],
    }),

    // ── Legacy endpoint (keep for backward compat) ──
    getLendersPolicies: builder.query<any, void>({
      query: () => '/v1/lenders/lenders-policies',
    }),
  }),
});

export const {
  useGetLenderRatesQuery,
  useGetLendersQuery,
  useGetLenderQuery,
  useCreateLenderMutation,
  useUpdateLenderMutation,
  useDeleteLenderMutation,
  useUploadLenderLogoMutation,
  useDeleteLenderLogoMutation,
  useGetLenderFeaturesQuery,
  useGetLenderFeaturesHistoryQuery,
  useCreateLenderFeaturesMutation,
  useUpdateLenderFeaturesMutation,
  useDeleteLenderFeaturesMutation,
  useGetLenderPolicyQuery,
  useGetLenderPolicyHistoryQuery,
  useCreateLenderPolicyMutation,
  useUpdateLenderPolicyMutation,
  useGetLenderProductsQuery,
  useCreateLenderProductMutation,
  useUpdateLenderProductMutation,
  useDeleteLenderProductMutation,
  useGetLenderProductPricingsQuery,
  useCreateLenderProductPricingMutation,
  useUpdateLenderProductPricingMutation,
  useDeleteLenderProductPricingMutation,
  useGetLendersPoliciesQuery,
  useGetProductProfilesComparisonQuery,
  useGetLenderProductProfilesQuery,
  useGetLenderProductProfilesHistoryQuery,
  useGetLenderProductProfileQuery,
  useCreateLenderProductProfileMutation,
  useUpdateLenderProductProfileMutation,
  useDeleteLenderProductProfileMutation,
} = lendersApi;
